import { ComponentProps } from "react";
import { useSnackbar } from "notistack";
import { useModal, UpdateModal } from "../../../components";
import { BankHoliday } from "../../../API";
import { useBankHolidayLib } from "../../../lib";
import { RequestModal } from "./RequestModal";

export const useRequestModals = () => {
  const {
    createBankHolidayRecord,
    updateBankHolidayRecord,
    deleteBankHolidayRecord,
  } = useBankHolidayLib();

  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const postAction = (
    message: string,
    isError: boolean,
    callback: () => void
  ) => {
    showSnackbar(message, isError);
    callback();
  };

  const { showModal: showCreateModal, hideModal: hideCreateModal } = useModal<
    ComponentProps<typeof RequestModal>,
    {
      bankHolidays?: BankHoliday[];
    }
  >({
    component: RequestModal,
    data: {
      title: "Create Bank Holiday",
      onSubmit: ({ data }) =>
        createBankHolidayRecord(data, {
          onSuccess: () =>
            postAction("Bank Holiday has been created", false, hideCreateModal),
          onError: () =>
            postAction(
              "Error creating Bank Holiday. Please try again in a few seconds",
              true,
              hideCreateModal
            ),
        }),
      testID: "create-request",
    },
  });

  const { showModal: showUpdateModal, hideModal: hideUpdateModal } = useModal<
    ComponentProps<typeof RequestModal>,
    {
      row: BankHoliday;
      bankHolidays: BankHoliday[];
    }
  >({
    component: RequestModal,
    data: {
      title: "Update Bank Holiday record",
      onSubmit: ({ data }) =>
        !data.id
          ? null
          : updateBankHolidayRecord(
              {
                ...data,
                id: data.id,
              },
              {
                onSuccess: () =>
                  postAction(
                    "Bank holiday has been updated.",
                    false,
                    hideUpdateModal
                  ),
                onError: () =>
                  postAction(
                    "Error updating Bank holiday. Please try again in a few seconds",
                    true,
                    hideUpdateModal
                  ),
              }
            ),
      testID: "update-request",
    },
  });

  const { showModal: showDeleteModal, hideModal: hideDeleteModal } = useModal<
    ComponentProps<typeof UpdateModal>,
    { row: BankHoliday }
  >({
    component: UpdateModal,
    data: {
      title: "Remove Bank holiday",
      label: "Remove",
      description: "Are you sure you want to remove this entry?",
      onUpdate: ({ id }) =>
        deleteBankHolidayRecord(id, {
          onSuccess: () =>
            postAction(
              "Bank holiday has been deleted.",
              false,
              hideDeleteModal
            ),
          onError: () =>
            postAction(
              "Error deleting Bank Holiday. Please try again in a few seconds",
              true,
              hideDeleteModal
            ),
        }),
    },
  });

  return {
    showCreateModal,
    showUpdateModal,
    showDeleteModal,
  };
};
